import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { modelOf } from '../../../prop-types';
import RouteService from '../../../services/RouteService';
import CategoryStore from '../../../store/CategoryStore';
import ConfigStore from '../../../store/ConfigStore';
import CustomNavigationLinkStore from '../../../store/CustomNavigationLinkStore';
import UIStore from '../../../store/UIStore';
import CustomNavigationLinkParentType from '../../../types/CustomNavigationLinkParentType';
import LinkType from '../../../types/LinkType';
import { transformInternalLink } from '../../../util/url';
import CategoryBadges from '../../common/CategoryBadges';
import NavigationMegaMenu from '../../navigation/NavigationMegaMenu';

const NavigationTopLevelMegaMenu = ({
  categoryStore,
  configStore,
  customNavigationLinkStore,
  debouncer,
  routeService,
  uiStore,
}) => {
  const getCategoryNonBlockedChildren = (category) => {
    if (!category) {
      return [];
    }

    return uiStore.megaMenu.activeSection
      ? category.getNonBlockedChildren(
          uiStore.megaMenu.activeSection.blockedCategoriesDict
        )
      : category.children;
  };

  const getCustomNavigationLinkTitle = (name) => {
    const twoLevelMegaMenuEnabled =
      configStore.navigation.megaMenu.twoLayerMegaMenuEnabled;

    if (twoLevelMegaMenuEnabled) {
      return (
        <span>
          <span>{name}</span>
        </span>
      );
    }

    return name;
  };

  const getCustomCategoryListPath = (customNavLink) => {
    if (customNavLink.link_type === LinkType.EXTERNAL_LINK) {
      return customNavLink.link;
    }

    if (customNavLink.link_type === LinkType.INTERNAL_LINK) {
      return routeService.getPath(transformInternalLink(customNavLink.link));
    }

    return routeService.getPath(
      customNavLink.link,
      uiStore.megaMenu.activeSection
    );
  };

  const convertCustomNavigationLinksToEntityItems = (
    topLevelCustomNavigationLinks
  ) => {
    return topLevelCustomNavigationLinks.map((topLevelCustomNavigationLink) => {
      return {
        id: topLevelCustomNavigationLink.id,
        label: getCustomNavigationLinkTitle(topLevelCustomNavigationLink.name),
        path: getCustomCategoryListPath(topLevelCustomNavigationLink),
        link_type: topLevelCustomNavigationLink.link_type,
        location: topLevelCustomNavigationLink.location,
        parent_sibling_id: topLevelCustomNavigationLink.parent_sibling_id,
        customNavLinks: convertCustomNavigationLinksToEntityItems(
          customNavigationLinkStore.findChildLinks(
            topLevelCustomNavigationLink.id
          )
        ),
      };
    });
  };

  const filterTopLevelCustomNavigations = (customNavigationLinks) => {
    if (!customNavigationLinks) {
      return null;
    }

    const topLevelCustomNavigationLinks = customNavigationLinks.filter(
      (customNavigationLinkSibling) =>
        !customNavigationLinkSibling.parent_sibling_id &&
        customNavigationLinkSibling.tree_type ===
          CustomNavigationLinkParentType.CATEGORY
    );

    return convertCustomNavigationLinksToEntityItems(
      topLevelCustomNavigationLinks
    );
  };

  const getCustomNavigationLinks = (category) => {
    const customNavItems = customNavigationLinkStore.filterByCategoryId(
      category.id
    );

    return convertCustomNavigationLinksToEntityItems(customNavItems);
  };

  const convertCategoryToNavigationEntity = (category) => {
    return {
      id: category.id,
      label: (
        <span>
          <span>{category.navigationName}</span>
          <CategoryBadges category={category} />
        </span>
      ),
      active: categoryStore.isInActiveCategoryHierarchy(category),
      path: routeService.getPath(category.path, uiStore.megaMenu.activeSection),
      children: getCategoryNonBlockedChildren(category).map(
        convertCategoryToNavigationEntity
      ),
      type: category.link_type,
      customNavLinks: getCustomNavigationLinks(category),
    };
  };

  const getMainLevelItems = () => {
    const { customNavigationLinks } = customNavigationLinkStore;

    let items = uiStore.megaMenu.visibleCategories.map(
      convertCategoryToNavigationEntity
    );

    const { rootCategory, activeSection } = uiStore.megaMenu;

    const twoLevelMegaMenuEnabled =
      configStore.navigation.megaMenu.twoLayerMegaMenuEnabled;

    // Don't show categoryOrSectionFields for custom navigation links
    if (
      (rootCategory && rootCategory.link_type) ||
      (activeSection && activeSection.link_type)
    ) {
      return items;
    }

    let categoryOrSectionFields = {};
    if (rootCategory && !twoLevelMegaMenuEnabled) {
      categoryOrSectionFields = {
        label: (
          <FormattedMessage
            id="navigation.shopAllCategory"
            defaultMessage="Shop all {category}"
            values={{
              category: uiStore.megaMenu.rootCategory.navigationName,
            }}
          />
        ),
        path: routeService.getPath(uiStore.megaMenu.rootCategory.path),
      };
    } else if (activeSection && !twoLevelMegaMenuEnabled) {
      categoryOrSectionFields = {
        label: (
          <FormattedMessage
            id="navigation.shopAllSection"
            defaultMessage="Shop all {section}"
            values={{
              section: uiStore.megaMenu.activeSection.navigationName,
            }}
          />
        ),
        path: routeService.getPath(uiStore.megaMenu.activeSection.path),
      };
    }

    const topLevelCustomNavigations = filterTopLevelCustomNavigations(
      customNavigationLinks
    );

    items = topLevelCustomNavigations.concat(items);

    if (!twoLevelMegaMenuEnabled) {
      items.unshift({
        ...categoryOrSectionFields,
        id: '_top',
      });
    }

    return items;
  };

  const { megaMenu } = uiStore;
  const centered = configStore.topNavigation.center;

  if (!megaMenu.isOpen) {
    return null;
  }

  const rootItem = megaMenu.activeSection || megaMenu.rootCategory;
  const items = getMainLevelItems();

  return (
    <NavigationMegaMenu
      // This onMouseOver prevents the onMouseOver that would close the MegaMenu
      onMouseOver={() => debouncer(() => null)}
      onMouseOut={() => debouncer(() => uiStore.megaMenu.close())}
      entities={items}
      close={megaMenu.close}
      key={rootItem.id}
      centered={centered}
    />
  );
};

NavigationTopLevelMegaMenu.propTypes = {
  categoryStore: modelOf(CategoryStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  customNavigationLinkStore: modelOf(CustomNavigationLinkStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  // This is a hack that we need in order to support onMouseOver and onMouseOut
  // when this megamenu and the elements toggling it are separate.
  // In normal (non-react) world this would be handled by rendering the
  // megamenu as a child of the menu item. This refactoring is however too big
  // right now, so we settle for having a global debouncer that only excutes the
  // last function call.
  debouncer: PropTypes.func,
};

export default inject(
  'categoryStore',
  'configStore',
  'customNavigationLinkStore',
  'routeService',
  'uiStore'
)(observer(NavigationTopLevelMegaMenu));
